/**
 * The review queue: finished missions waiting for a person to judge them, oldest first, cleared
 * like a code review inbox. Each row: the item (kind, key, title), what changed (the branch, and
 * whether it landed), acceptance criteria met n of m, checks, when it finished; on hover Accept
 * and Send back. Pick several to accept them at once. Opening one goes to the mission page.
 *
 * Accept moves the item to Done (and closes the mission as done if nobody had). Send back moves
 * the item to Ready (Backlog for a spike), so another mission can pick it up.
 */
import { Check, CircleCheck, Eye, GitBranch, Undo2 } from '@orgus/ui/icons';
import { useCallback, useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import { ITEM_WORKFLOW } from '@orgus/protocol';
import type { BacklogItem, Id, ItemStatus } from '@orgus/protocol';
import { Banner, BulkBar, Button, Checkbox, EmptyState, ErrorState, IconButton, Panel, Pressable, REVEAL_GROUP, RelativeTime, RevealRest, RevealSlot, Tooltip, cn, formatInt, plural, toast, useSelection } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { toUiError } from '../../lib/api.ts';
import { bulkUpdate } from '../requirements/data.ts';
import { KeyLabel } from '../requirements/parts.tsx';
import { updateMission } from './data.ts';
import { finishedAt } from './model.ts';
import type { MissionRow } from './model.ts';
import { KindIcon } from './parts.tsx';

/** What a reviewer needs at a glance: criteria met, the branch, whether the landing queue's checks passed. */
export function reviewFacts(r: MissionRow): { met: number; of: number; branch: string | null; landed: boolean } {
  const criteria = r.item?.acceptance ?? [];
  // The finished mission's report decides; without one (or an empty one), what a person checked off.
  const report = r.mission.report && !r.mission.report.blocked && r.mission.report.acceptance.length ? r.mission.report.acceptance : null;
  const met = report ? criteria.filter((c) => report.some((a) => a.id === c.id && a.met)).length : criteria.filter((c) => c.done).length;
  const launch = [...r.mission.launches].reverse().find((l) => l.worktree);
  const branch = r.mission.landed?.branch ?? (launch?.worktree ? launch.worktree.split(/[\\/]/).pop() ?? null : null);
  return { met, of: criteria.length, branch, landed: !!r.mission.landed };
}

/** Where Send back puts an item: Ready, or the first step of a workflow without it. */
export function sendBackStatus(item: Pick<BacklogItem, 'kind'>): ItemStatus {
  const flow = ITEM_WORKFLOW[item.kind];
  return flow.includes('ready') ? 'ready' : flow[0]!;
}

type Verdict = 'accept' | 'back';

export function ReviewMissions({ queue, projectName, showProject, onOpen }: { queue: readonly MissionRow[]; projectName: (id: Id) => string; showProject: boolean; onOpen: (missionId: Id) => void }) {
  const order = useMemo(() => queue.map((r) => r.mission.id), [queue]);
  const sel = useSelection(order);
  const [busy, setBusy] = useState<Set<Id>>(new Set());
  const [error, setError] = useState<UiError | null>(null);
  const [skipped, setSkipped] = useState<string | null>(null);

  const judge = useCallback(
    async (rows: readonly MissionRow[], verdict: Verdict) => {
      const ids = rows.map((r) => r.mission.id);
      setBusy((b) => new Set([...b, ...ids]));
      setError(null);
      setSkipped(null);
      try {
        const items = rows.filter((r) => r.item).map((r) => r.item!);
        const reasons: string[] = [];
        if (verdict === 'accept') {
          const r = await bulkUpdate({ ids: items.map((i) => i.id), patch: { status: 'done' } });
          reasons.push(...r.skipped.map((s) => `${items.find((i) => i.id === s.id)?.key ?? s.id}: ${s.reason}`));
          for (const row of rows) if (!row.mission.closed && r.updated.includes(row.item!.id)) await updateMission(row.mission.id, { closed: 'done' });
        } else {
          for (const status of new Set(items.map(sendBackStatus))) {
            const group = items.filter((i) => sendBackStatus(i) === status);
            const r = await bulkUpdate({ ids: group.map((i) => i.id), patch: { status } });
            reasons.push(...r.skipped.map((s) => `${group.find((i) => i.id === s.id)?.key ?? s.id}: ${s.reason}`));
          }
        }
        const done = rows.length - reasons.length;
        if (reasons.length) setSkipped(reasons.slice(0, 3).join(' '));
        if (done > 0) toast.success(verdict === 'accept' ? `Accepted ${plural(done, 'item')}` : `Sent back ${plural(done, 'item')}`, verdict === 'accept' ? 'Moved to Done.' : 'Ready for another mission.');
        sel.clear();
      } catch (e) {
        setError(toUiError(e));
      } finally {
        setBusy((b) => new Set([...b].filter((id) => !ids.includes(id))));
      }
    },
    [sel],
  );

  const picked = queue.filter((r) => sel.isSelected(r.mission.id));
  const anyChecked = sel.count > 0;
  return (
    <Panel flush className={cn(anyChecked && 'mb-16')}>
      <header className="flex h-row items-center gap-2 px-row-x">
        <h2 className="text-ui font-medium text-fg">Missions to review</h2>
        {queue.length > 0 && <span className="text-caption text-fg-subtle num">{formatInt(queue.length)}</span>}
        {queue.length > 0 && <span className="ml-auto text-caption text-fg-subtle">Oldest first</span>}
      </header>
      {error && <ErrorState variant="inline" error={error} className="mx-4 mb-2" action={<Button variant="ghost" size="sm" onClick={() => setError(null)}>Dismiss</Button>} />}
      {skipped && (
        <Banner tone="attention" title="Some items stayed as they were" className="mx-4 mb-2" actions={<Button variant="ghost" size="sm" onClick={() => setSkipped(null)}>Dismiss</Button>}>
          {skipped}
        </Banner>
      )}
      {queue.length === 0 ? (
        <EmptyState icon={<CircleCheck />} title="Nothing to review" description="Finished missions wait here until you accept them or send them back." />
      ) : (
        <ul className="flex flex-col px-2 pb-2" data-list="" aria-label="Missions to review">
          {queue.map((r) => {
            const f = reviewFacts(r);
            const checked = sel.isSelected(r.mission.id);
            const working = busy.has(r.mission.id);
            const item = r.item!;
            const open = (e: MouseEvent) => {
              if (e.shiftKey || e.metaKey || e.ctrlKey) {
                e.preventDefault();
                sel.toggle(r.mission.id, e.shiftKey);
                return;
              }
              onOpen(r.mission.id);
            };
            return (
              <li key={r.mission.id} className={cn(REVEAL_GROUP, 'group/rrow motion-arrive relative rounded-control')}>
                <Pressable
                  data-row=""
                  data-review-row={r.mission.id}
                  onClick={open}
                  aria-label={`Review ${item.key} ${item.title}`}
                  className={cn('flex min-h-row items-center gap-3 rounded-control px-row-x py-2 transition-colors sm:h-row sm:py-0 duration-(--duration-fast)', checked ? 'selected-tint' : 'hover:bg-hover')}
                >
                  <span className={cn('flex w-4 shrink-0 justify-center transition-opacity duration-(--duration-fast)', anyChecked ? 'opacity-0' : 'pointer-fine:group-hover/rrow:opacity-0 pointer-fine:group-focus-within/rrow:opacity-0')}>
                    <KindIcon kind={item.kind} tooltip={false} />
                  </span>
                  <KeyLabel value={item.key} className="hidden w-16 sm:inline" />
                  <span className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
                    <span className="line-clamp-3 min-w-0 shrink text-ui font-medium text-fg wrap-anywhere sm:line-clamp-none sm:truncate">{item.title}</span>
                    {f.branch && (
                      <span data-meta="" className="hidden min-w-0 shrink items-center gap-1 truncate text-caption text-fg-subtle md:inline-flex" title={f.landed ? `Landed ${f.branch}` : f.branch}>
                        <GitBranch className="size-3 shrink-0" aria-hidden />
                        <span className="truncate font-mono">{f.branch}</span>
                      </span>
                    )}
                    {showProject && (
                      <span data-meta="" className="hidden shrink-0 text-caption text-fg-subtle lg:inline">
                        {projectName(r.mission.projectId)}
                      </span>
                    )}
                  </span>
                  <span className="hidden w-24 shrink-0 justify-end md:flex" data-column="criteria">
                    {f.of > 0 && (
                      <Tooltip content={`${formatInt(f.met)} of ${formatInt(f.of)} acceptance criteria met`}>
                        <span tabIndex={-1} className="text-caption text-fg-subtle num">
                          Criteria <span className={f.met === f.of ? 'text-fg-muted' : 'text-fg'}>{formatInt(f.met)}</span> of {formatInt(f.of)}
                        </span>
                      </Tooltip>
                    )}
                  </span>
                  <span className="hidden w-20 shrink-0 justify-end md:flex" data-column="checks">
                    {f.landed ? (
                      <span className="inline-flex items-center gap-1 text-caption text-fg-muted">
                        <CircleCheck className="size-3 text-status-success" aria-hidden />
                        Checks
                      </span>
                    ) : (
                      <span className="text-caption text-fg-subtle" aria-label="No checks yet">
                        –
                      </span>
                    )}
                  </span>
                  {/* The time ends at the row's padding, like Updated on the Missions tab; the actions show just before it, never over it. */}
                  <RevealRest className="w-16 text-caption text-fg-subtle num sm:w-20" data-column="finished">
                    <RelativeTime ts={finishedAt(r.mission)} />
                  </RevealRest>
                </Pressable>
                <span className={cn('absolute inset-y-0 left-row-x flex items-center', !anyChecked && 'pointer-fine:opacity-0 pointer-fine:group-hover/rrow:opacity-100 pointer-fine:group-focus-within/rrow:opacity-100')}>
                  <Checkbox aria-label={`Select ${item.key}`} checked={checked} tabIndex={-1} onClick={(e) => e.stopPropagation()} onCheckedChange={(_, e) => sel.toggle(r.mission.id, (e.nativeEvent as globalThis.MouseEvent).shiftKey)} />
                </span>
                <RevealSlot className="absolute inset-y-0 right-row-x hidden items-center gap-1 sm:flex">
                  <IconButton size="xs" label={`Accept ${item.key}`} icon={<Check />} loading={working} onClick={() => void judge([r], 'accept')} />
                  <IconButton size="xs" label={`Send ${item.key} back`} icon={<Undo2 />} disabled={working} onClick={() => void judge([r], 'back')} />
                  <IconButton size="xs" label={`Open the review of ${item.key}`} icon={<Eye />} onClick={() => onOpen(r.mission.id)} />
                </RevealSlot>
              </li>
            );
          })}
        </ul>
      )}
      <BulkBar
        count={sel.count}
        noun="mission"
        total={queue.length}
        onSelectAll={sel.toggleAll}
        onClear={sel.clear}
        actions={[
          { id: 'accept', label: `Accept ${plural(picked.length, 'item')}`, icon: <Check />, disabled: !picked.length || busy.size > 0, onClick: () => void judge(picked, 'accept') },
          { id: 'back', label: 'Send back', icon: <Undo2 />, disabled: !picked.length || busy.size > 0, onClick: () => void judge(picked, 'back') },
        ]}
      />
    </Panel>
  );
}
